import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  DeliveryIcon,
  Divider,
  EmptyState,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Text,
} from '@citibox-panel/ui';
import { track } from '@citibox-panel/analytics';
import type {
  CarrierOption,
  Delivery,
  LocationDeliveriesQuery,
  LocationDeliveriesResponse,
  LocationMatch,
} from '@citibox-panel/api-client';
// Through the slice's feature barrel — the one route `location-search` publishes for it. The slice
// graph stays acyclic: `location-search` never imports back.
import { LocationSearchDialog } from '../../location-search';
import { useSearchTabs } from '../../search-shell/hooks/useSearchTabs';
import {
  locationIdFromTabKey,
  locationTabKey,
  type LocationFilters,
  type LocationTab,
} from '../../search-shell/model/shellState';
import { DeliveryRow } from '../../user-deliveries/components/DeliveryRow';
import { countBucket } from '../../../lib/countBucket';
import { DELIVERY_COLUMN_HEADERS } from '../../../lib/deliveryColumns';
import { LOCATION_RECIPIENT_COLUMN_HEADERS } from '../lib/locationDeliveryColumns';
import { useNoticeOnError } from '../../search-shell/hooks/useErrorNotice';
import { useLocationDeliveries } from '../hooks/useLocationDeliveries';
import { markSearchReported } from '../lib/reportedSearches';
import { LocationFilterChips } from './LocationFilterChips';

export interface LocationDeliveriesLayoutProps {
  search: LocationTab;
  fetchLocationDeliveries: (
    id: number,
    query: LocationDeliveriesQuery,
  ) => Promise<LocationDeliveriesResponse>;
  searchLocations: (q: string) => Promise<LocationMatch[]>;
  fetchLocationCarriers: (locationId: number) => Promise<CarrierOption[]>;
  fetchDeliveryStates: () => Promise<string[]>;
}

// The failure's copy and the source it is keyed by (ADR 0021). Named by the TYPE of list — «del
// buzón» — and never by the building: a postal address in a notice that floats over whatever the
// agent opens next is exactly what the decision refused. Accepted cost: two building tabs failing
// at once read identically.
// TODO: i18n
const READ_ERROR = 'No se pudieron cargar las entregas del buzón.';
const NOTICE_ID = 'location-deliveries';

// The section title, and the only thing that tells an agent their search resolved. Two properties
// carry that:
//
// - It is a PERSISTENT live region: mounted in every state, «Entregas» while the read is out and
//   «Entregas · N» once it settles, N included when it is `0`. A region that arrives together with
//   its text is the update assistive tech misses, and «Entregas · 0» is itself the answer to a
//   confirmed search — which is why the empty state below is silenced rather than left to announce.
// - `count` is the number of rows RENDERED. The response also carries a `count`, and reading it
//   would claim a completeness the table has not shown: nobody has yet said whether this read is
//   capped, and «Entregas · 240» over fifty rows is a claim an agent would act on.
function DeliveriesHeader({ count }: { count: number | undefined }) {
  return (
    <Box role="status" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <DeliveryIcon fontSize="small" />
      {/* TODO: i18n */}
      <Text variant="subtitle2" bold>
        {count === undefined ? 'Entregas' : `Entregas · ${String(count)}`}
      </Text>
    </Box>
  );
}

export function LocationDeliveriesLayout({
  search,
  fetchLocationDeliveries,
  searchLocations,
  fetchLocationCarriers,
  fetchDeliveryStates,
}: LocationDeliveriesLayoutProps) {
  // «Editar» opens it; local UI state only.
  const [dialogOpen, setDialogOpen] = useState(false);
  // A ref and not state, because nothing here may render on its own: the render that matters is the
  // one `onExited` triggers, and it has to land after MUI has removed the modal.
  const confirmed = useRef<{ location: LocationMatch; filters: LocationFilters } | null>(null);
  const headingRef = useRef<HTMLElement>(null);
  // The carrier LABEL is display-only and stays out of the read: dropped here rather than ignored
  // downstream, so it can neither ride a request nor split the query cache the day backend renames
  // a carrier. What is left is exactly the query the endpoint takes.
  const { carrierLabel: _label, ...query } = search.filters;
  const { data, isPending, isError } = useLocationDeliveries(
    locationIdFromTabKey(search.key),
    query,
    fetchLocationDeliveries,
  );
  useNoticeOnError(isError, NOTICE_ID, READ_ERROR);
  const { openDelivery, openLocation, setLocationFilters } = useSearchTabs();

  const openIds = new Set(search.deliveries.map((d) => d.id));

  // Only when focus is sitting on `document.body`, i.e. nowhere. That guard is what makes this safe
  // rather than rude: clicking a tab in the strip leaves focus on the tab button, and stealing it
  // from there would break the arrow-key navigation between tabs.
  useEffect(() => {
    if (document.activeElement !== document.body) return;
    headingRef.current?.focus();
  }, []);

  // One view per (tab, filter set) for the page's lifetime — editing the filters IS a new search
  // and re-reports, while returning from a delivery sub-tab is not a search at all, and this
  // component remounts on both. `markSearchReported` CONSUMES the key as a side effect of being
  // asked, so it is reached LAST: called on a failed fetch it would mark a search that was never
  // reported, and the successful retry would then report nothing at all.
  useEffect(() => {
    if (isPending || data === undefined) return;
    if (!markSearchReported(search.key, search.filters)) return;
    track('location_deliveries_viewed', {
      days: String(search.filters.days),
      // A boolean ABOUT the carrier filter, never the id: with the filter optional, «is the list
      // narrowed at all» is the question, and an id is high-cardinality for no answer it buys.
      has_carrier_filter: String(search.filters.carrier !== undefined),
      // A boolean ABOUT the box filter, never the box number: `box` arrives absent or non-empty,
      // so its presence and «the agent applied a box filter» are the same fact.
      has_box_filter: String(search.filters.box !== undefined),
      // A boolean and a count ABOUT the state filter, never the states: which ones the agent marked
      // is their question, not a dimension — the same rule that keeps the carrier id out. Both are
      // strings because the analytics props are.
      has_state_filter: String(search.filters.states !== undefined),
      state_count: String(search.filters.states?.length ?? 0),
      // Buckets the rows the screen showed, so the dimension describes what the agent saw.
      count_bucket: countBucket(data.deliveries.length),
    });
  }, [isPending, data, search.key, search.filters]);

  const handleOpen = (delivery: Delivery): void => {
    // The sub-tab label is the delivery's tracking, set at open time; a null tracking falls back
    // to the shell's "Entrega {short id}". openDelivery does not relabel an already-open tab.
    openDelivery(search.key, delivery.id, delivery.tracking ?? undefined);
    track('delivery_opened_from_list', {
      shared: String(delivery.sharedWithRecipient),
      status: delivery.status,
      // Names the list the row was opened from, so this drill-in is attributable to the address
      // search rather than pooled with the «Usuario» list's.
      source: 'location',
    });
  };

  // Reported after a successful copy, with the same categorical props as the open event — no PII:
  // the copied tracking never reaches analytics, and there is no phone in the payload to leak.
  const handleCopied = (delivery: Delivery): void => {
    track('delivery_copied_from_list', {
      shared: String(delivery.sharedWithRecipient),
      status: delivery.status,
      source: 'location',
    });
  };

  // REMEMBERS. Everything a confirm causes happens on the way out — see `handleExited` — so this
  // touches nothing outside itself. Closing is included rather than left to the dialog's own
  // `onClose`, so the handler is self-contained; the two are one `setState` to the same value.
  const handleConfirm = (location: LocationMatch, filters: LocationFilters): void => {
    confirmed.current = { location, filters };
    setDialogOpen(false);
  };

  // ACTS, at the one moment MUI has removed the modal AND stripped `aria-hidden` off the page, so
  // the re-query this triggers changes «Entregas · N» where it can be announced.
  const handleExited = (): void => {
    const pending = confirmed.current;
    // Consumed, so a later cancel-close cannot replay a building the agent already left.
    confirmed.current = null;
    // A close that confirmed nothing — Escape, «Cancelar», the ✕ — has nothing to replay.
    if (pending === null) return;
    const { location, filters } = pending;
    // Compared through `locationTabKey` because a tab's KEY and a location's ID are deliberately
    // different strings. «Cambiar» stays reachable from «Editar», so a confirm can come back with
    // another building: that one gets its own tab rather than repointing this one, whose heading,
    // label and queried id would otherwise disagree with each other.
    if (search.key === locationTabKey(location.id)) setLocationFilters(search.key, filters);
    else openLocation(location.id, location.address, filters);
  };

  return (
    <>
      {/* The screen is a raised WHITE surface on the page's grey `background.default`, not content
          lying flush on it — the same card the «Usuario» ficha sits on, so the two entity views a
          CC agent switches between read as one product.
          `flexGrow` takes the height the shell has left rather than stopping under the last row:
          a building with one delivery is the common case, and a stub of card floating on grey
          reads as a screen that failed to finish loading. Where the host is not a full-height
          flex column — the UI-kit catalogue — there is no free space and it is a no-op. */}
      <Card variant="outlined" sx={{ borderRadius: 3, m: 2, flexGrow: 1 }}>
        {/* No CardContent padding: each region pads its own content, so the divider between them
            runs edge to edge of the card (the rounded overflow clips its ends) — the «Usuario»
            card's arrangement, for the same reason. */}
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          {/* WHAT the agent is looking at: the address and the filters that produced the list, on
              one line. The chips answer «which query is this?» where the question is asked, and
              «Editar» — the whole affordance for changing them — sits with what it edits. Wraps
              rather than truncates: a long address beside three chips is a narrow window, not a
              reason to hide either. */}
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              columnGap: 2,
              rowGap: 1,
              p: 3,
            }}
          >
            {/* A real focus target: `openLocation` unmounts this layout together with the «Editar»
                MUI restores focus to, so a keyboard agent arrives here with focus on `<body>`.
                Set like the «Usuario» card sets its name — the h6 step, bold, on a 1.2 line —
                because the two are the same thing on two screens: the subject of the detail the
                agent opened. Not left on the browser's bold default, and without a pin: the sub-tab
                directly above already carries one for this address.

                A raw Box and not the façade's `Text` because this is a real focus target, and
                `Text` neither forwards a ref nor takes `tabIndex`. */}
            <Box
              component="h2"
              tabIndex={-1}
              ref={headingRef}
              sx={{ typography: 'h6', fontWeight: 'bold', lineHeight: 1.2, m: 0 }}
            >
              {search.label}
            </Box>
            <LocationFilterChips
              filters={search.filters}
              onEdit={() => {
                setDialogOpen(true);
              }}
            />
          </Box>
          <Divider />
          {/* Collapse the overline header labels' tall 2.66 line-height so the column-header row
              isn't oversized, and bold them (overline appears only in the table head here). */}
          <Stack
            spacing={2}
            sx={{ p: 3, '& .MuiTypography-overline': { lineHeight: 1.2, fontWeight: 'bold' } }}
          >
            <DeliveriesHeader count={data?.deliveries.length} />
            {isPending ? (
              <Stack spacing={1}>
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} width="100%" height={32} />
                ))}
              </Stack>
            ) : isError ? (
              // Nothing in place: the notice above said it, and the pattern gives it no action
              // (ADR 0021). The tab, its heading and its chips stay — a remount reloads the list.
              null
            ) : // Keyed on the rows, exactly as the header's count is: a response whose `count` disagrees
            // with its list must paint this rather than a head with nothing under it. An undefined `data`
            // is unreachable here — settled and not in error leaves success or failure — and degrades to
            // the same visual rather than to a headless table.
            data === undefined || data.deliveries.length === 0 ? (
              // The visual only: the section header above already announced «Entregas · 0» from a
              // region that existed before the result did, and a second one saying the same thing is
              // the announcement doubled or lost.
              // TODO: i18n
              <EmptyState title="No hay entregas con estos filtros" announce={false} />
            ) : (
              // TODO: i18n
              <Table aria-label="Entregas">
                <TableHead>
                  <TableRow>
                    {/* Empty, over `DeliveryRow`'s shared-delivery flag — always blank on a
                        building-wide list, which has no owner «Usuario» to share a delivery with. */}
                    <TableCell component="th" scope="col" />
                    {DELIVERY_COLUMN_HEADERS.map((header) => (
                      <TableCell key={header} component="th" scope="col">
                        {/* `text.primary`: the muted secondary tone measures ≈2.4:1 on this
                            background, below AA. */}
                        <Text variant="overline">{header}</Text>
                      </TableCell>
                    ))}
                    {/* Two more labels, from a constant of this slice's own: the shared array is
                        the contract with the «Usuario» card's head, and a label added there would
                        sit over a cell that list does not render. */}
                    {LOCATION_RECIPIENT_COLUMN_HEADERS.map((header) => (
                      <TableCell key={header} component="th" scope="col">
                        <Text variant="overline">{header}</Text>
                      </TableCell>
                    ))}
                    {/* Trailing header cell for the copy-button column, empty like the leading one. */}
                    <TableCell component="th" scope="col" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.deliveries.map((delivery) => (
                    <DeliveryRow
                      key={delivery.id}
                      delivery={delivery}
                      // Marks what the agent already opened. Read off the tab's own sub-tabs rather than
                      // re-looked-up through the hook: this prop is the same state, already narrowed.
                      selected={openIds.has(delivery.id)}
                      onOpen={() => {
                        handleOpen(delivery);
                      }}
                      // A building-wide list has no account «Usuario», so there is no account
                      // phone to copy. It stays empty rather than being dropped: the titular's own
                      // phone is what fills that column here, and it comes from the row below.
                      phone=""
                      // The two fields spelled out rather than `delivery.recipient` passed through.
                      // The prop's type hides the «Usuario» id, but the OBJECT would still carry it
                      // at runtime, and nothing renders or needs it — naming the two fields keeps it
                      // from crossing into the component at all, so no later spread can carry it
                      // into a telemetry payload. It stays in the schema, where a link to the ficha
                      // will find it.
                      recipient={{
                        phone: delivery.recipient?.phone ?? null,
                        name: delivery.recipient?.name ?? null,
                      }}
                      onCopied={() => {
                        handleCopied(delivery);
                      }}
                    />
                  ))}
                </TableBody>
              </Table>
            )}
          </Stack>
        </CardContent>
      </Card>
      {/* Outside the card: it portals to the body anyway, and a closed dialog left inside would
          make «which node is CardContent's last child» depend on whether it is open. */}
      <LocationSearchDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
        }}
        onExited={handleExited}
        onConfirm={handleConfirm}
        // Seeded with this tab's building and filters, so «Editar» lands on the filters step
        // rather than on a coincidences search for an address the agent already has.
        initialLocation={{ id: locationIdFromTabKey(search.key), address: search.label }}
        initialFilters={search.filters}
        searchLocations={searchLocations}
        fetchLocationCarriers={fetchLocationCarriers}
        fetchDeliveryStates={fetchDeliveryStates}
      />
    </>
  );
}
